import React from 'react'
import { ReleasesPage } from './pages/ReleasesPage.jsx'
import { NotificationsPage } from './pages/NotificationsPage.jsx'
import { GuidePage } from './pages/GuidePage.jsx'
import { FAQPage } from './pages/FAQPage.jsx'
import reportGuideHtml from 'virtual:wiki/guide-reports'
import managerGuideHtml from 'virtual:wiki/guide-managers'
import adminGuideHtml from 'virtual:wiki/guide-admins'
import securityComplianceHtml from 'virtual:wiki/security-compliance'
import privacyAndDataHtml from 'virtual:wiki/privacy-and-data'
import gettingStartedHtml from 'virtual:wiki/getting-started'
import sessionsAndCardsHtml from 'virtual:wiki/sessions-and-cards'
import encryptionStandardsHtml from 'virtual:wiki/encryption-standards'

const NAV_ITEMS = [
  { id: 'releases', label: "What's new" },
  { id: 'notifications', label: 'Notifications' },
  { id: 'report', label: 'For reports' },
  { id: 'manager', label: 'For managers' },
  { id: 'faq', label: 'FAQ' },
]

// Map a markdown file basename (e.g. "guide-reports") to the docs page id
// it should resolve to. Used by GuidePage's link interceptor so that
// internal `[link](./other-file.md)` references navigate inside the SPA
// instead of letting the browser follow the relative URL into a 404.
const MD_LINK_TO_PAGE = {
  'guide-reports': 'report',
  'guide-managers': 'manager',
  'guide-admins': 'admin',
  'security-compliance': 'security-compliance',
  // Hidden pages — reachable only via in-page links, no nav tab.
  'privacy-and-data': 'privacy-and-data',
  'getting-started': 'getting-started',
  'sessions-and-cards': 'sessions-and-cards',
  'encryption-standards': 'encryption-standards',
}

export function DocsLayout({ page, onNavigate }) {
  const handleMdLink = (id) => onNavigate(MD_LINK_TO_PAGE[id] ?? page)

  return (
    <div style={styles.wrapper}>
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              style={{ ...styles.tab, ...(page === item.id ? styles.tabActive : {}) }}
              onClick={() => onNavigate(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>
      <div style={styles.content}>
        {page === 'releases' && <ReleasesPage />}
        {page === 'notifications' && <NotificationsPage />}
        {page === 'report' && <GuidePage title="Guide for reports" subtitle="How to get the most out of Soundcheck as a report." html={reportGuideHtml} onMdLink={handleMdLink} />}
        {page === 'manager' && <GuidePage title="Guide for managers" subtitle="How to use Soundcheck to support your reports effectively." html={managerGuideHtml} onMdLink={handleMdLink} />}
        {page === 'admin' && <GuidePage title="Guide for admins" subtitle="Managing your organization's Soundcheck instance." html={adminGuideHtml} onMdLink={handleMdLink} />}
        {page === 'security-compliance' && <GuidePage title="For InfoSec" subtitle="For ISO / IPO and auditors. Access control, encryption, Cognito policies, and known gaps." html={securityComplianceHtml} onMdLink={handleMdLink} />}
        {page === 'privacy-and-data' && <GuidePage title="Privacy and your data" subtitle="What Soundcheck stores, who can see it, and how to export or delete it." html={privacyAndDataHtml} onMdLink={handleMdLink} />}
        {page === 'getting-started' && <GuidePage title="Getting started" subtitle="First sign-in, MFA setup, and finding your way around." html={gettingStartedHtml} onMdLink={handleMdLink} />}
        {page === 'sessions-and-cards' && <GuidePage title="Sessions and cards" subtitle="The two core concepts in Soundcheck." html={sessionsAndCardsHtml} onMdLink={handleMdLink} />}
        {page === 'encryption-standards' && <GuidePage title="Encryption standards" subtitle="Encryption at rest, in transit, and known gaps." html={encryptionStandardsHtml} onMdLink={handleMdLink} />}
        {page === 'faq' && <FAQPage />}
      </div>
    </div>
  )
}

const styles = {
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    background: 'var(--bg)',
  },
  nav: {
    padding: '16px 32px 4px',
    borderBottom: '1px solid var(--border)',
  },
  navInner: {
    display: 'flex',
    gap: '4px',
    maxWidth: '720px',
    margin: '0 auto',
    overflowX: 'auto',
  },
  tab: {
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  tabActive: {
    color: 'var(--text-primary)',
    fontWeight: 600,
    borderBottomColor: 'var(--text-primary)',
  },
  content: {
    padding: '24px 32px',
    maxWidth: '720px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
}
